import { Close, MailOutline } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import SelectControl from "components/form/SelectControl";
import TextFieldControl from "components/form/TextFieldControl";
import VALIDATION from "helpers/validation";
import { cleanSpaceInString } from "helpers/validation/createValidation";
import TEAM_ROLE_OPTIONS from "pages/Company/components/TeamTab/constants";
import { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FIELD_LABEL_PROPS, SELECT_FIELD_SX } from "../constants";
import { CompanyRegisterFormValues } from "../types";
import StepSection from "./StepSection";

type TeamStepProps = {
  isCreateCompanyLoading: boolean;
  onCreateCompany: () => void;
  onPreviousStep: () => void;
  roleLabelMap: Record<string, string>;
};

const TeamStep = ({ isCreateCompanyLoading, onCreateCompany, onPreviousStep, roleLabelMap }: TeamStepProps) => {
  const { clearErrors, control, getValues, setError, setValue, trigger, watch } =
    useFormContext<CompanyRegisterFormValues>();
  const { append, fields, remove } = useFieldArray({
    control,
    keyName: "fieldId",
    name: "teamMembers",
  });

  const watchedTeamEmail = watch("teamEmail");
  const watchedTeamPosition = watch("teamPosition");
  const watchedTeamRole = watch("teamRole");

  useEffect(() => {
    clearErrors("teamEmail");
  }, [clearErrors, watchedTeamEmail]);

  useEffect(() => {
    clearErrors("teamPosition");
  }, [clearErrors, watchedTeamPosition]);

  useEffect(() => {
    clearErrors("teamRole");
  }, [clearErrors, watchedTeamRole]);

  const handleAddTeamMember = async () => {
    clearErrors(["teamEmail", "teamPosition", "teamRole"]);

    const isValid = await trigger(["teamEmail", "teamPosition", "teamRole"]);
    if (!isValid) {
      return;
    }

    const teamEmail = cleanSpaceInString(getValues("teamEmail"));
    const teamPosition = cleanSpaceInString(getValues("teamPosition"));
    const teamRole = getValues("teamRole");

    if (!teamRole) {
      return;
    }

    if (fields.some((member) => member.email.toLowerCase() === teamEmail.toLowerCase())) {
      setError("teamEmail", {
        type: "manual",
        message: "Сотрудник с таким email уже добавлен",
      });
      return;
    }

    append({
      email: teamEmail,
      position: teamPosition,
      role: teamRole,
    });

    setValue("teamEmail", "");
    setValue("teamPosition", "");
    setValue("teamRole", "");
  };

  const handleCreateCompanyClick = () => {
    clearErrors(["teamEmail", "teamPosition", "teamRole"]);
    onCreateCompany();
  };

  return (
    <StepSection
      title={
        <Box>
          <Typography
            component='span'
            sx={{ color: "#081B3A", fontWeight: 700, fontSize: { xs: 32, sm: 36 }, verticalAlign: "sub" }}>
            Команда
          </Typography>
          <Typography
            component='span'
            sx={{ color: "#081B3A", fontWeight: 700, fontSize: { xs: 18, sm: 20 } }}>
            {" "}
            — опционально
          </Typography>
        </Box>
      }
      subtitle='Выдайте доступы коллегам — им придёт приглашение. Можно добавить их и позже.'>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)" },
          gap: 1.5,
          alignItems: "start",
        }}>
        <TextFieldControl
          name='teamEmail'
          label='Почта'
          type='email'
          variant='outlined'
          labelTypographyProps={FIELD_LABEL_PROPS}
          placeholder='colleague@company.ru'
          rules={{
            required: true,
            validate: (value) => VALIDATION.emailFull.validate(cleanSpaceInString(String(value || ""))),
          }}
        />

        <TextFieldControl
          name='teamPosition'
          label='Должность'
          variant='outlined'
          labelTypographyProps={FIELD_LABEL_PROPS}
          placeholder='Например, HR-менеджер'
          rules={{ required: true, ...VALIDATION.position }}
        />

        <SelectControl
          name='teamRole'
          label='Роль'
          placeholder='Выберите роль'
          items={TEAM_ROLE_OPTIONS}
          valueName='value'
          labelName='label'
          variant='outlined'
          sx={{ ...SELECT_FIELD_SX, width: "100%", "& .MuiSelect-select": { height: 40, boxSizing: "border-box" } }}
          rules={{ required: true }}
          labelProps={FIELD_LABEL_PROPS}
          endAction={
            <Button
              variant='outlined'
              size='large'
              sx={{ minWidth: 138 }}
              onClick={handleAddTeamMember}>
              Добавить
            </Button>
          }
        />
      </Box>

      <Stack spacing={1.5}>
        {fields.length > 0 ? (
          fields.map((member, index) => (
            <Stack
              key={member.fieldId}
              direction='row'
              spacing={2}
              alignItems='center'
              sx={{
                py: 1,
                px: 2,
                borderRadius: 2,
                border: "1px solid #D9E4FF",
                backgroundColor: "#F3F7FF",
              }}>
              <MailOutline sx={{ color: "#8DA0C9" }} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='body16rg'
                  sx={{ color: "#081B3A", fontWeight: 500, wordBreak: "break-word", fontSize: 18 }}>
                  {member.email}
                </Typography>
                <Typography
                  variant='body14rg'
                  sx={{ mt: 0.5, color: "#5B6F97", wordBreak: "break-word" }}>
                  {member.position}
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1.5,
                  backgroundColor: "#EDF2FF",
                  color: "#2147AF",
                  border: "1px solid #7499FF",
                  fontSize: 14,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}>
                {roleLabelMap[member.role]}
              </Box>

              <IconButton
                onClick={() => remove(index)}
                sx={{ color: "#8DA0C9" }}>
                <Close />
              </IconButton>
            </Stack>
          ))
        ) : (
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2.5,
              border: "1px solid #D5E0FF",
              backgroundColor: "#FBFCFF",
            }}>
            <Typography
              variant='body16rg'
              sx={{ color: "#7A8DB5", lineHeight: 1.6 }}>
              Пока нет добавленных сотрудников. Вы можете создать компанию сейчас и вернуться к приглашениям позже.
            </Typography>
          </Box>
        )}
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems='center'
        spacing={1.5}>
        <Button
          variant='text'
          size='large'
          sx={{ minWidth: 96, px: 0, color: "#1C3366", fontSize: 18, fontWeight: 500 }}
          onClick={onPreviousStep}>
          Назад
        </Button>

        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-end'
          sx={{ flex: 1, width: "100%" }}
          spacing={1.5}>
          <LoadingButton
            loading={isCreateCompanyLoading}
            variant='contained'
            size='large'
            sx={{ minWidth: { xs: "100%", sm: 224 } }}
            onClick={handleCreateCompanyClick}>
            Создать компанию
          </LoadingButton>
        </Stack>
      </Stack>
    </StepSection>
  );
};

export default TeamStep;
